import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ProfileSummary, type User } from '../api.js';
import { useAuth } from '../auth.js';
import { Icon } from '../components/Icon.js';
import { Topbar } from './AdminLayout.js';
import { BRAND } from '../branding.js';
import { ROLE_LABEL, isSuperadmin, type Role } from '../utils/roles.js';

function randomPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const arr = new Uint32Array(14);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => alphabet[n % alphabet.length]).join('');
}

interface Form {
  name: string;
  email: string;
  role: Role;
  password: string;
  allowedProfileIds: string[];
}

const empty: Form = {
  name: '',
  email: '',
  role: 'manager',
  password: '',
  allowedProfileIds: [],
};

export function UserEdit(): JSX.Element {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const nav = useNavigate();
  const { user: me } = useAuth();

  const [form, setForm] = useState<Form>({ ...empty, password: randomPassword() });
  const [profiles, setProfiles] = useState<ProfileSummary[] | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  // Сколько профилей пользователю выдали другие админы: их видит и меняет
  // только суперадмин (сервер отдаёт админу лишь его часть доступа).
  const [otherProfilesCount, setOtherProfilesCount] = useState(0);

  useEffect(() => {
    api
      .listProfiles()
      .then((list) => setProfiles(list.map((p) => ({ id: p.id, name: p.name, isActive: p.isActive }))))
      .catch(() => setProfiles([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api
      .getUser(id!)
      .then((u: User) => {
        setForm({
          name: u.name,
          email: u.email,
          role: u.role,
          password: '',
          allowedProfileIds: u.allowedProfileIds,
        });
        setOtherProfilesCount(u.otherProfilesCount);
        setLoading(false);
      })
      .catch((e: Error) => {
        setErr(e.message);
        setLoading(false);
      });
  }, [id, isNew]);

  const update = <K extends keyof Form>(k: K, v: Form[K]): void =>
    setForm((s) => ({ ...s, [k]: v }));

  const toggleProfile = (pid: string, checked: boolean): void => {
    setForm((s) => ({
      ...s,
      allowedProfileIds: checked
        ? [...s.allowedProfileIds, pid]
        : s.allowedProfileIds.filter((x) => x !== pid),
    }));
  };

  // Список профилей нужен админу и менеджеру; суперадмин видит всё.
  const usesProfiles = form.role === 'admin' || form.role === 'manager';

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (isNew) {
        const created = await api.createUser({
          name: form.name,
          email: form.email,
          // На установке «только Google» пароля нет: пользователь входит через Google.
          ...(BRAND.passwordLogin ? { password: form.password } : {}),
          role: form.role as 'superadmin' | 'admin' | 'manager',
          allowedProfileIds: usesProfiles ? form.allowedProfileIds : [],
        });
        nav(`/admin/users/${created.id}`, { replace: true });
      } else {
        // Роль волонтёра в админке не меняется — её и не отправляем.
        await api.updateUser(id!, {
          name: form.name,
          email: form.email,
          ...(form.role === 'volunteer'
            ? {}
            : {
                role: form.role,
                allowedProfileIds: usesProfiles ? form.allowedProfileIds : [],
              }),
        });
        nav('/admin/users');
      }
    } catch (e) {
      const d = (e as { details?: unknown }).details;
      setErr(typeof d === 'string' ? d : JSON.stringify(d));
    } finally {
      setBusy(false);
    }
  };

  const onSendReset = async (): Promise<void> => {
    if (!confirm('Отправить пользователю письмо со ссылкой для сброса пароля?')) return;
    setErr(null);
    try {
      await api.sendUserPasswordReset(id!);
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!confirm('Удалить пользователя?')) return;
    try {
      await api.deleteUser(id!);
      nav('/admin/users');
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>Загрузка…</div>;

  const isSelf = !isNew && me?.id === id;
  const actorIsSuper = isSuperadmin(me?.role);
  // Админ не меняет данные менеджера, у которого есть профили других админов:
  // сменив email на свой, он вошёл бы в чужие профили.
  const sharedForMe = !actorIsSuper && otherProfilesCount > 0;

  return (
    <>
      <Topbar
        title={isNew ? 'Новый пользователь' : <>Пользователь: <bdi>{form.name || form.email}</bdi></>}
        actions={
          <>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => nav('/admin/users')}
            >
              Отмена
            </button>
            <button
              type="submit"
              form="user-form"
              className="btn btn-primary btn-sm"
              disabled={
                busy ||
                !form.name.trim() ||
                !form.email.trim() ||
                (isNew && BRAND.passwordLogin && form.password.length < 8)
              }
            >
              {isNew ? 'Создать' : 'Сохранить'}
            </button>
          </>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <form id="user-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 14, maxWidth: 720 }}>
          {err && (
            <div
              className="card"
              style={{
                padding: 14,
                color: 'var(--danger-fg)',
                background: 'var(--danger-subtle-bg)',
              }}
            >
              {err}
            </div>
          )}
          {resetSent && (
            <div
              className="card"
              style={{
                padding: 14,
                color: 'var(--ok-fg)',
                background: 'var(--ok-subtle-bg)',
              }}
            >
              Письмо со ссылкой для сброса отправлено.
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="card-title">Данные пользователя</div>
            </div>
            <div className="card-body stack">
              <div className="grid-2">
                <div className="field">
                  <label className="field-label">Имя *</label>
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Иван Иванов"
                    disabled={sharedForMe}
                  />
                </div>
                <div className="field">
                  <label className="field-label">Email *</label>
                  <input
                    className="input"
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    disabled={sharedForMe}
                  />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Роль</label>
                <select
                  className="select"
                  value={form.role}
                  onChange={(e) => update('role', e.target.value as Role)}
                  // Админ заводит только менеджеров; свою роль не меняет никто;
                  // волонтёров заводят вне админки.
                  disabled={isSelf || !actorIsSuper || form.role === 'volunteer'}
                  title={
                    isSelf
                      ? 'Нельзя изменить свою роль'
                      : !actorIsSuper
                        ? 'Админ заводит только менеджеров'
                        : undefined
                  }
                  style={{ maxWidth: 240 }}
                >
                  <option value="manager">{ROLE_LABEL.manager}</option>
                  {(actorIsSuper || form.role === 'admin') && (
                    <option value="admin">{ROLE_LABEL.admin}</option>
                  )}
                  {(actorIsSuper || form.role === 'superadmin') && (
                    <option value="superadmin">{ROLE_LABEL.superadmin}</option>
                  )}
                  {form.role === 'volunteer' && (
                    <option value="volunteer">{ROLE_LABEL.volunteer}</option>
                  )}
                </select>
                <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 4 }}>
                  {form.role === 'superadmin' &&
                    'Видит все профили и всех пользователей, создаёт профили и выдаёт к ним доступ.'}
                  {form.role === 'admin' &&
                    'Работает только в отмеченных профилях: настройки, продукты, флоу, промокоды. Ведёт менеджеров этих профилей.'}
                  {form.role === 'manager' &&
                    'Продукты, флоу и промокоды в отмеченных профилях. Настройки профилей и пользователи ему закрыты.'}
                  {form.role === 'volunteer' && 'Только чек-ин на входе.'}
                </div>
              </div>
              {isNew && !BRAND.passwordLogin && (
                <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                  Пароль не нужен: пользователь входит через Google аккаунтом с этим email. Приглашение
                  уйдёт на почту.
                </div>
              )}
              {isNew && BRAND.passwordLogin && (
                <div className="field">
                  <label className="field-label">Пароль (8+ символов) *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input mono"
                      type="text"
                      minLength={8}
                      value={form.password}
                      onChange={(e) => update('password', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => update('password', randomPassword())}
                    >
                      <Icon name="refresh" size={13} /> Сгенерировать
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 4 }}>
                    Будет отправлен в welcome-письме.
                  </div>
                </div>
              )}
            </div>
          </div>

          {usesProfiles && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Доступ к профилям</div>
              </div>
              <div className="card-body stack">
                {profiles === null && (
                  <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>Загрузка…</div>
                )}
                {profiles && profiles.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>
                    {actorIsSuper
                      ? 'Нет ни одного профиля. Создай его через «+ Профиль» в переключателе профиля.'
                      : 'Вам ещё не выдали ни одного профиля — попросите суперадмина.'}
                  </div>
                )}
                {profiles &&
                  profiles.map((p) => (
                    <label
                      key={p.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.allowedProfileIds.includes(p.id)}
                        onChange={(e) => toggleProfile(p.id, e.target.checked)}
                      />
                      <span>
                        <bdi>{p.name}</bdi>
                      </span>
                      {!p.isActive && (
                        <span
                          className="chip no-dot chip-neutral"
                          style={{ fontSize: 10.5, height: 18, padding: '0 6px' }}
                        >
                          выкл.
                        </span>
                      )}
                    </label>
                  ))}
                {otherProfilesCount > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                    Ещё профилей у других админов: {otherProfilesCount}. Их видит и меняет только
                    суперадмин; ваши галочки их не затрагивают.
                  </div>
                )}
                {profiles &&
                  profiles.length > 0 &&
                  form.allowedProfileIds.length + otherProfilesCount === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--danger-fg)' }}>
                    Без отмеченных профилей пользователь не сможет работать в админке.
                  </div>
                )}
              </div>
            </div>
          )}

          {!isNew && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Действия</div>
              </div>
              <div className="card-body stack">
                <div className="row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {BRAND.passwordLogin && !sharedForMe && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => void onSendReset()}
                    >
                      <Icon name="mail" size={13} />
                      Отправить ссылку для сброса пароля
                    </button>
                  )}
                  {!isSelf && !sharedForMe && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost btn-danger"
                      onClick={() => void onDelete()}
                    >
                      <Icon name="trash" size={13} />
                      Удалить пользователя
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </>
  );
}
